import { useQuery } from '@tanstack/react-query';
import { CreatorFilterState } from '@blisspointmedia/bpm-types/dist/YoutubeCreatorInsights';
import {
  GetCreatorInsightsRequestBody,
  GetCreatorInsightsResponse
} from '@blisspointmedia/bpm-types/dist/YoutubeCreatorSets';
import { awaitJSON, YoutubePlanningLambdaFetch } from '@src/utils/fetch-utils';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { PAGE_TITLE } from '../../youtube-planning-constants';
import { GoogleAdsAccount } from '../../youtube-planning-types';
import { ViewerDetails } from '../creator-discovery-constants';
import {
  buildCreatorInsightsCriteria,
  fromCreatorFilterState,
  getSupplementalData,
  isSearchable,
  toCreatorRows
} from '../creator-discovery-utils';

// Records how many results share viewer data.
const trackSearchResults = (search: CreatorFilterState, response: GetCreatorInsightsResponse) => {
  const rows = toCreatorRows(response);
  Mixpanel.track(MxE.CreatorSearchResults, {
    page: PAGE_TITLE,
    search_mode: fromCreatorFilterState(search).mode,
    creators: rows.length,
    sharing_viewer_data: rows.filter(row => row.viewerDetails === ViewerDetails.SHARED).length,
    local_creators: rows.filter(row => row.isLocal).length
  });
};

// Runs only for a submitted search (the Search button, or a shared link), never as the user edits:
// every call is a billable Google request and the service allows 5 per second. Not retried, since
// the lambda's 429/403/400 are final and written for the user.
export const useCreatorInsights = (
  cid: string,
  googleAdsAccount: GoogleAdsAccount | undefined,
  search: CreatorFilterState | undefined
): ReturnType<typeof useQuery<GetCreatorInsightsResponse>> =>
  useQuery({
    queryKey: ['youtubeCreatorInsights', cid, googleAdsAccount?.account_id, search],
    queryFn: async (): Promise<GetCreatorInsightsResponse> => {
      if (!googleAdsAccount || !search) {
        throw new Error('A Google Ads account and a search are required.');
      }
      const supplementalData = getSupplementalData(search);
      const body: GetCreatorInsightsRequestBody = {
        company: cid,
        googleAdsAccount: googleAdsAccount.account_id,
        mccId: googleAdsAccount.mcc_id,
        ...(supplementalData ? { supplementalData } : {}),
        ...buildCreatorInsightsCriteria(search, googleAdsAccount.account_id)
      };
      const res = await YoutubePlanningLambdaFetch('/getCreatorInsights', {
        method: 'POST',
        body
      });
      const response = await awaitJSON<GetCreatorInsightsResponse>(res);
      trackSearchResults(search, response);
      return response;
    },
    enabled: Boolean(cid && googleAdsAccount && search && isSearchable(search)),
    retry: false,
    // Returning to an earlier search (back/forward) reuses its result instead of re-billing.
    staleTime: Infinity
  });
